/**
 * RFC-110: DOCX Preview Component
 *
 * Renders DOCX pages with their original styles and optional backlink highlighting.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, Download } from 'lucide-react';
import { getAuthHeaders, trpc } from '@/lib/trpc';
import { SERVER_URL } from '@/lib/env';
import { sanitizePreviewHtml } from '@/lib/sanitize-preview-html';
import { isStorageObjectUrl, withInlineStorageUrl } from '@/components/compliance/StorageTextFilePreview';
import { Button } from '@deepguide-ai/ui/button';

interface DocxPreviewProps {
  url: string;
  filename: string;
  highlightParagraphs?: number[];
  highlightText?: string;
  onReady?: () => void;
}

// Normalize text for matching: lower case, collapse whitespace, replace nbsp
function buildNormalized(text: string) {
  let normalized = '';
  const map: number[] = [];
  let inSpace = false;

  for (let i = 0; i < text.length; i++) {
    const rawChar = text[i] === '\u00a0' ? ' ' : text[i];
    const lower = rawChar.toLowerCase();
    if (/\s/.test(lower)) {
      if (!inSpace) {
        normalized += ' ';
        map.push(i);
      }
      inSpace = true;
    } else {
      normalized += lower;
      map.push(i);
      inSpace = false;
    }
  }

  return { normalized, map };
}

type TextSegment = {
  node: Text;
  text: string;
  start: number;
};

function flattenTextNodes(root: HTMLElement) {
  const segments: TextSegment[] = [];
  let combined = '';

  const walk = (node: Node) => {
    if (node.nodeName === 'STYLE' || node.nodeName === 'SCRIPT') return;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      segments.push({ node: node as Text, text, start: combined.length });
      combined += text;
      return;
    }

    if (node.nodeName === 'BR') {
      combined += ' ';
      return;
    }

    node.childNodes.forEach((child) => walk(child));
  };

  walk(root);

  return { segments, combined };
}

function highlightHtmlWithText(html: string, query: string) {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return { html, matched: false };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
  const body = doc.body;

  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return { html, matched: false };
  }

  const { segments, combined } = flattenTextNodes(body);
  const combinedNormalized = buildNormalized(combined);
  const queryNormalized = buildNormalized(trimmedQuery).normalized;

  if (!queryNormalized || segments.length === 0) {
    return { html, matched: false };
  }

  const seen = new Set<string>();
  const ranges: Array<{ start: number; end: number }> = [];

  const addRange = (normStr: string, idx: number) => {
    if (idx === -1 || normStr.length === 0) return;
    const rawStart = combinedNormalized.map[idx];
    const rawEnd = combinedNormalized.map[idx + normStr.length - 1] + 1;
    const key = `${rawStart}-${rawEnd}`;
    if (!seen.has(key)) {
      seen.add(key);
      ranges.push({ start: rawStart, end: rawEnd });
    }
  };

  // Try full query first
  addRange(queryNormalized, combinedNormalized.normalized.indexOf(queryNormalized));

  // If no full match, split by newlines as sub-pieces
  if (ranges.length === 0) {
    const parts = trimmedQuery
      .split(/\s*\n+\s*/)
      .map((p) => p.trim())
      .filter((p) => p.length >= 6);

    for (const part of parts) {
      const normPart = buildNormalized(part).normalized;
      addRange(normPart, combinedNormalized.normalized.indexOf(normPart));
    }
  }

  if (ranges.length === 0) {
    return { html, matched: false };
  }

  // Apply highlights per text node using collected ranges
  const sortedRanges = ranges.sort((a, b) => a.start - b.start);

  segments.forEach((segment) => {
    const segStart = segment.start;
    const segEnd = segment.start + segment.text.length;
    const overlaps = sortedRanges.filter(
      (r) => r.end > segStart && r.start < segEnd
    );

    if (overlaps.length === 0) return;

    const parent = segment.node.parentNode;
    if (!parent) return;

    let cursor = 0;
    const frag = doc.createDocumentFragment();

    overlaps.forEach((range, idx) => {
      const localStart = Math.max(0, range.start - segStart);
      const localEnd = Math.min(segment.text.length, range.end - segStart);

      if (localStart > cursor) {
        frag.appendChild(
          doc.createTextNode(segment.text.slice(cursor, localStart))
        );
      }

      if (localEnd > localStart) {
        const mark = doc.createElement('mark');
        mark.setAttribute(
          'style',
          'background-color: rgb(254 249 195); padding: 2px 4px; border-radius: 2px;'
        );
        mark.textContent = segment.text.slice(localStart, localEnd);
        frag.appendChild(mark);
      }

      cursor = localEnd;

      // If this is the last overlap and there's trailing text
      if (idx === overlaps.length - 1 && cursor < segment.text.length) {
        frag.appendChild(
          doc.createTextNode(segment.text.slice(cursor))
        );
      }
    });

    parent.replaceChild(frag, segment.node);
  });

  return { html: body.innerHTML, matched: true };
}

const PREVIEW_FRAME_HTML = `<!doctype html><html><head>
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src blob: data:; font-src blob: data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
<style>html,body{margin:0}body{overflow:auto}.docx-wrapper{background:transparent}</style>
</head><body></body></html>`;

export function DocxPreview({
  url,
  filename,
  highlightParagraphs,
  highlightText,
  onReady,
}: DocxPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const renderedContentRef = useRef<HTMLDivElement | null>(null);
  const originalHtmlRef = useRef('');
  const [frameReady, setFrameReady] = useState(false);
  const [renderedUrl, setRenderedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const useServerPreview = /[?&]X-Amz-Signature=/i.test(url);

  // Paragraph indexes come from the existing canonical Mammoth extraction.
  // Fetch this metadata only for backlinks that actually need it.
  const previewQuery = useQuery({
    ...trpc.evidencePack.getDocxPreview.queryOptions({ url }),
    enabled: useServerPreview || !!highlightParagraphs?.length,
    staleTime: 1000 * 60 * 30,
  });

  const serverHtml = useServerPreview ? previewQuery.data?.html : undefined;
  const serverError = useServerPreview ? previewQuery.error : null;

  useEffect(() => {
    const frame = frameRef.current;
    const frameDocument = frame?.contentDocument;
    if (!frameReady || !frame || !frameDocument) return;

    const controller = new AbortController();
    let resizeObserver: ResizeObserver | undefined;
    setRenderedUrl(null);
    setError(null);
    renderedContentRef.current = null;
    originalHtmlRef.current = '';
    frameDocument.body.replaceChildren();

    (async () => {
      try {
        const content = frameDocument.createElement('div');
        if (useServerPreview) {
          // Legacy signed links cannot be fetched cross-origin or have their
          // query changed. Preserve the existing authenticated HTML fallback.
          if (serverError) throw serverError;
          if (serverHtml === undefined) return;
          content.innerHTML = sanitizePreviewHtml(serverHtml);
        } else {
          const sourceUrl = new URL(url, window.location.origin);
          const authenticatedStorage = (isStorageObjectUrl(url)
            || sourceUrl.pathname.startsWith('/api/storage/auditor-vendor/'))
            && (sourceUrl.origin === new URL(SERVER_URL).origin || sourceUrl.origin === window.location.origin);
          const headers = authenticatedStorage ? await getAuthHeaders() : undefined;
          const [response, { renderAsync }] = await Promise.all([
            fetch(withInlineStorageUrl(url), {
              credentials: authenticatedStorage ? 'include' : 'omit',
              headers,
              signal: controller.signal,
            }),
            import('docx-preview'),
          ]);
          if (!response.ok) throw new Error(`Failed to load file (${response.status})`);
          const data = await response.arrayBuffer();
          if (controller.signal.aborted) return;

          // Render offscreen so a slow previous document cannot replace the
          // current document when its asynchronous renderer finishes.
          await renderAsync(data, content, content, {
            breakPages: true,
            ignoreLastRenderedPageBreak: false,
            renderHeaders: true,
            renderFooters: true,
            renderAltChunks: false,
            useBase64URL: true,
          });
        }
        if (controller.signal.aborted) return;

        frameDocument.body.replaceChildren(content);
        renderedContentRef.current = content;
        originalHtmlRef.current = content.innerHTML;

        const fitWidth = () => {
          const pages = content.querySelectorAll<HTMLElement>('section.docx');
          const pageWidth = Math.max(0, ...Array.from(pages, (page) => page.offsetWidth));
          if (pageWidth > 0 && frame.clientWidth > 0) {
            content.style.setProperty('zoom', String(Math.min(1, Math.max(0.1, (frame.clientWidth - 60) / pageWidth))));
          }
        };
        fitWidth();
        resizeObserver = new ResizeObserver(fitWidth);
        resizeObserver.observe(frame);
        setRenderedUrl(url);
        onReadyRef.current?.();
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load preview');
        }
      }
    })();

    return () => {
      controller.abort();
      resizeObserver?.disconnect();
    };
  }, [frameReady, url, useServerPreview, serverHtml, serverError]);

  useEffect(() => {
    const content = renderedContentRef.current;
    if (!content || renderedUrl !== url) return;

    let html = originalHtmlRef.current;
    let matchedTextHighlight = false;
    if (highlightText?.trim()) {
      const result = highlightHtmlWithText(html, highlightText);
      html = result.html;
      matchedTextHighlight = result.matched;
    }

    if (!matchedTextHighlight && highlightParagraphs?.length) {
      for (const index of highlightParagraphs) {
        const paragraph = previewQuery.data?.paragraphs?.[index];
        if (!paragraph) continue;
        const paragraphText = new DOMParser().parseFromString(paragraph.text, 'text/html').body.textContent ?? '';
        html = highlightHtmlWithText(html, paragraphText).html;
      }
    }

    content.innerHTML = html;
    content.querySelector('mark')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [url, renderedUrl, highlightText, highlightParagraphs, previewQuery.data]);

  const loading = renderedUrl !== url && !error;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-md border">
      <div className="flex h-[50px] shrink-0 items-center justify-between gap-3 border-b bg-background px-4">
        <span className="min-w-0 truncate text-xs text-muted-foreground" title={filename}>
          {filename}
        </span>
        <Button variant="ghost" size="sm" className="shrink-0" asChild>
          <a href={url} download={filename}>
            <Download className="mr-1 h-4 w-4" />
            Download
          </a>
        </Button>
      </div>
      <div className="relative min-h-0 flex-1">
        <iframe
          ref={frameRef}
          title={`Word preview: ${filename}`}
          sandbox="allow-same-origin"
          srcDoc={PREVIEW_FRAME_HTML}
          onLoad={() => setFrameReady(true)}
          className="h-full w-full border-0 bg-muted"
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-background text-sm text-muted-foreground" role="status">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading document...
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background p-6 text-center" role="alert">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm text-destructive">Failed to load preview</span>
            <span className="text-xs text-muted-foreground">{error}</span>
            <span className="text-xs text-muted-foreground">Download the original file to view it.</span>
          </div>
        )}
      </div>
    </div>
  );
}
